import React, {useState, useEffect} from 'react';
import {View, Text, TextInput, TouchableOpacity} from 'react-native';
import {MaterialIcons} from '@expo/vector-icons';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import styles, {
  infoIconStyle,
  lockIconSize,
  lockedRowText,
  placeholderColor
} from '../../../style/adminTools';
import RuleEngineDropdown from '../../rulesEngine/RuleEngineDropdown';
import TooltipBubble from './TooltipBubble';
import useLayoutTier from './useLayoutTier';
import useSettingSave from './useSettingSave';
import HighlightedLabel from './HighlightedLabel';

const toText = value =>
  value === undefined || value === null ? '' : String(value);

const DECIMAL = /^[+-]?(\d+\.?\d*|\.\d+)$/;

const validateNumber = (text, min, max) => {
  const trimmed = text.trim();
  const parsed = DECIMAL.test(trimmed) ? Number(trimmed) : NaN;
  if (!Number.isFinite(parsed))
    return {error: I18n.t('adminTools.invalidNumber')};
  if (min !== undefined && parsed < min)
    return {error: I18n.t('adminTools.minValue', {min})};
  if (max !== undefined && parsed > max)
    return {error: I18n.t('adminTools.maxValue', {max})};
  return {value: parsed};
};

const SettingInputRow = ({
  settingKey,
  label,
  tooltip,
  type,
  value,
  options = [],
  min,
  max,
  onSave,
  highlight = false,
  highlightQuery,
  locked = false,
  disabled = false,
  onSaveStateChange
}) => {
  const stacked = useLayoutTier() === 'mobile';
  const [showTooltip, setShowTooltip] = useState(false);
  const [text, setText] = useState(toText(value));
  const [validationError, setValidationError] = useState(null);
  const {draft, pending, error, save} = useSettingSave({
    settingKey,
    value,
    onSave,
    onSaveStateChange
  });
  const id = `setting-${settingKey}`;
  const frozen = disabled || pending || locked;

  useEffect(() => {
    setText(toText(draft));
  }, [draft]);

  const commit = () => {
    if (frozen || text === toText(draft)) return;
    if (type === 'number') {
      const result = validateNumber(text, min, max);
      if (result.error) {
        setValidationError(result.error);
        return;
      }
      setValidationError(null);
      if (result.value !== draft) save(result.value);
      return;
    }
    save(text);
  };

  const onChangeText = next => {
    setText(next);
    if (validationError) setValidationError(null);
  };

  const shownError = validationError || error;

  return (
    <View
      style={[
        styles.settingRow,
        stacked ? styles.settingRowPhone : null,
        highlight ? styles.settingRowMatch : null,
        locked ? styles.settingRowLocked : null
      ]}
      testID={id}
    >
      {showTooltip && tooltip && stacked ? (
        <TooltipBubble full text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      <View
        style={[
          styles.settingRowMain,
          stacked ? styles.settingRowMainStacked : null
        ]}
        testID={`${id}-main`}
      >
        <View
          style={[
            styles.settingLabelWrap,
            stacked ? styles.settingLabelWrapStacked : null
          ]}
        >
          <HighlightedLabel
            text={label}
            query={highlightQuery}
            style={[
              styles.settingLabel,
              locked ? styles.settingLabelLocked : null
            ]}
            testID={`${id}-label`}
          />
          {locked ? (
            <MaterialIcons
              name="lock"
              size={lockIconSize}
              color={lockedRowText}
              testID={`${id}-lock`}
            />
          ) : null}
          {tooltip ? (
            <TouchableOpacity
              onPress={() => setShowTooltip(!showTooltip)}
              style={[
                styles.settingInfoButton,
                stacked ? styles.settingInfoButtonPhone : null
              ]}
              accessibilityLabel={I18n.t('adminTools.aboutSetting', {label})}
              testID={`${id}-info`}
            >
              <i className="icon-info-circled" style={infoIconStyle} />
            </TouchableOpacity>
          ) : null}
          {pending ? (
            <Text style={styles.settingSavingText}>
              {I18n.t('unsavedChanges.saving')}
            </Text>
          ) : null}
        </View>
        <View
          style={
            stacked ? styles.settingControlStacked : styles.settingControlInline
          }
          testID={`${id}-control`}
        >
          {type === 'select' ? (
            <View
              style={frozen ? styles.settingInputLocked : null}
              pointerEvents={frozen ? 'none' : 'auto'}
              testID={`${id}-select-wrap`}
            >
              <RuleEngineDropdown
                options={options}
                selectedValue={draft}
                onSelect={next => {
                  if (!frozen && next !== draft) save(next);
                }}
                testID={`${id}-dropdown`}
                error={!!shownError}
                triggerStyle={stacked ? styles.selectTriggerPhone : undefined}
              />
            </View>
          ) : (
            <TextInput
              style={[
                type === 'number'
                  ? styles.settingInputNumber
                  : styles.settingInputText,
                stacked ? styles.settingInputPhone : null,
                locked ? styles.settingInputLockedText : null,
                shownError ? styles.settingInputInvalid : null
              ]}
              value={text}
              onChangeText={onChangeText}
              onBlur={commit}
              onSubmitEditing={commit}
              editable={!frozen}
              keyboardType={type === 'number' ? 'numeric' : 'default'}
              placeholderTextColor={placeholderColor}
              accessibilityLabel={label}
              testID={`${id}-input`}
            />
          )}
        </View>
      </View>
      {showTooltip && tooltip && !stacked ? (
        <TooltipBubble text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      {shownError ? (
        <Text style={styles.settingErrorText} testID={`${id}-error`}>
          {shownError}
        </Text>
      ) : null}
    </View>
  );
};

SettingInputRow.propTypes = {
  settingKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  type: PropTypes.oneOf(['number', 'text', 'select']).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired
    })
  ),
  min: PropTypes.number,
  max: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  highlightQuery: PropTypes.string,
  locked: PropTypes.bool,
  disabled: PropTypes.bool,
  onSaveStateChange: PropTypes.func
};

export default SettingInputRow;
